import { useCallback, useMemo, useState } from 'react';
import { EventDraftContext } from './EventDraftContext.js';

/**
 * Holds the per-event scratch state the scrolling body and the pinned decision
 * footer — siblings in the split view — have to share:
 *
 * - `draft`: the inline-edit value (the prototype edits the prepared output in
 *   place rather than in a modal; the body owns the field, the footer owns
 *   Cancel / Confirm Send).
 * - `itemDecisions`: per-row decisions for list-based types, keyed by row id.
 *   Gov Promotion and Gov Review store each claim's Approve / Reject / Correct
 *   here so the footer can gate Submit on them.
 * - `itemCorrections`: saved correction text keyed by claim id.
 * - `breakGlassApprovers`: live two-key gate rows for Break Glass (body + footer).
 *
 * Mount one per selected event (`key={event.id}`) so switching events drops any
 * unsaved state instead of leaking it into the next one.
 */
function EventDraftProvider({ children }) {
  // `null` means "not editing" — an empty string is a legitimate draft.
  const [draft, setDraft] = useState(null);
  const [itemDecisions, setItemDecisions] = useState({});
  const [itemCorrections, setItemCorrections] = useState({});
  const [breakGlassApprovers, setBreakGlassApprovers] = useState(null);

  const startEdit = useCallback((initialValue = '') => setDraft(initialValue), []);
  const cancelEdit = useCallback(() => setDraft(null), []);

  const setItemDecision = useCallback((itemId, decision, correctedText = null) => {
    setItemDecisions((current) => {
      if (decision !== undefined) return { ...current, [itemId]: decision };
      const remaining = { ...current };
      delete remaining[itemId];
      return remaining;
    });
    setItemCorrections((current) => {
      if (typeof correctedText === 'string') return { ...current, [itemId]: correctedText };
      if (!(itemId in current)) return current;
      const remaining = { ...current };
      delete remaining[itemId];
      return remaining;
    });
  }, []);

  const value = useMemo(
    () => ({
      editing: draft !== null,
      draft: draft ?? '',
      startEdit,
      setDraft,
      cancelEdit,
      itemDecisions,
      itemCorrections,
      setItemDecision,
      setItemDecisions,
      breakGlassApprovers,
      setBreakGlassApprovers,
    }),
    [
      draft,
      startEdit,
      cancelEdit,
      itemDecisions,
      itemCorrections,
      setItemDecision,
      breakGlassApprovers,
    ],
  );

  return <EventDraftContext.Provider value={value}>{children}</EventDraftContext.Provider>;
}

export default EventDraftProvider;
